import { Action, action, createStore, persist, Thunk, thunk } from 'easy-peasy'
import gameViewModel, { GameViewModel, GameViewState } from '../features/gameView/GameViewModel'
import { PuzzleQuestModel, PuzzleQuestViewModel } from './../features/PuzzleQuest/PuzzleQuestModel'

import { WebSocketModel, WebsocketModel } from '../API/WebsocketModel'
import { RatingType } from '../components/matchmaking/ChallengeCard'
import AnalysisViewModel, { GameAnalysisModel } from '../features/analysisView/AnalysisViewModel'
import { BasePGNHeaders } from '../features/analysisView/utils'
import { DailyBonusModel } from '../features/DailyBonusService/DailyBonusModel'
import { EmotesModel } from '../features/gameView/emotes/EmotesModel'
import matchMakingConnectionModel, {
    MatchMakingConnectionModel,
} from '../features/matchMakingDrawer/matchMakingConnector/MatchMakingConnectorModel'
import { MissionsServiceModel } from '../features/MissionService/MissionsModel'
import ObserveViewModel, { ObserveViewConnectionModel } from '../features/ObserveView/ObserveViewModel'
import { PopupServiceModel } from '../features/PopupService/PopupServiceModel'
import ProfileViewModel, { ProfileModel } from '../features/Profile/ProfileViewModel'
import { DailyPuzzleV2Model, DailyPuzzleViewV2Model } from '../features/Puzzles/DailyPuzzle/DailyPuzzleViewModel'
import { PuzzlePracticeModel, PuzzlePracticeViewModel } from '../features/Puzzles/PuzzlePractice/PuzzlePracticeViewModel'
import DailyPuzzleViewModel, { DailyPuzzleModel } from '../features/PuzzlesView/DailyPuzzleViewModel'
import PuzzlesViewModel, { PuzzlesModel } from '../features/PuzzlesView/PuzzlesViewModel'
import { isGuest } from '../helpers/user'
import GetCookie from '../sharedComponents/src/globalHeader/common/getCookie'
import {
    ensureLogin,
    getUserFollowList,
    guestRegistration,
    tokenCookieKey,
    UserSearchResult,
} from '../sharedComponents/src/globalHeader/functions/ams-functions'
import {
    GroupNames,
    LoggedInUserData,
    LoginState,
    Rules,
    UserData,
} from '../sharedComponents/src/globalHeader/GlobalHeader'
import { analyticsManager } from '../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { boardColors } from '../sharedComponents/src/globalHeader/theme/colors'
import { MMChallengeType, PGNHeader } from './types'

export type QueueTimeStart = {
    challengeId: string
    challengeType: MMChallengeType
    timeMode: string
    rated: boolean
    timestamp: number
    pairId?: string
}

export interface StoreModel {
    // stores
    analysisMode: GameAnalysisModel
    gameView: GameViewModel
    emotes: EmotesModel
    observeView: ObserveViewConnectionModel
    puzzlesView: PuzzlesModel
    dailyPuzzleView: DailyPuzzleModel
    puzzleQuestView: PuzzleQuestModel
    puzzlePracticeView: PuzzlePracticeModel
    dailyPuzzleViewV2: DailyPuzzleV2Model
    popupService: PopupServiceModel
    missionsService: MissionsServiceModel
    dailyBonus: DailyBonusModel
    matchMaker: MatchMakingConnectionModel
    ws: WebSocketModel
    profileView: ProfileModel

    // state
    // TODO: change this name since it is no longer a drawer
    drawerOpen: 'open' | 'closed' | 'loading'
    isDrawerMenuOpen: boolean
    serverConnectionData: boolean
    darkMode: boolean
    activeTab: number
    activeAnalysisTab: number
    userData: LoggedInUserData
    rules: Rules[]
    userGroups: GroupNames[]
    followList: Array<UserSearchResult>
    PGNHeader: PGNHeader
    unreadMessages: number
    queueTimeStarts: QueueTimeStart[]
    token: string | undefined
    snackbarMessage: string | undefined
    manualActiveFTUE: string
    previousBackground: string

    // actions
    setServerConnectionData: Action<StoreModel, boolean>
    setToken: Action<StoreModel, string | undefined>
    setDrawerOpen: Action<StoreModel, 'open' | 'closed' | 'loading'>
    setIsDrawerMenuOpen: Action<StoreModel, boolean>
    setDarkMode: Action<StoreModel, boolean>
    setActiveTab: Action<StoreModel, number>
    setUserData: Action<StoreModel, LoggedInUserData>
    updateUserData: Action<StoreModel, UserData>
    setRules: Action<StoreModel, Rules[]>
    setUserGroups: Action<StoreModel, GroupNames[]>
    setActiveAnalysisTab: Action<StoreModel, number>
    setPGNHeader: Action<StoreModel, PGNHeader>
    setUnreadMessages: Action<StoreModel, number>
    setUpdatedRating: Action<StoreModel, { type: RatingType; newRating: number | undefined }>
    setSnackbarMessage: Action<StoreModel, string | undefined>
    addQueueTimeStart: Action<StoreModel, QueueTimeStart>
    removeQueueTimeStart: Action<StoreModel, string>
    updateQueueTimeStart: Action<StoreModel, { challengeId: string; pairId: string }>
    clearQueueTimeStarts: Action<StoreModel>
    setUserFollowList: Action<StoreModel, Array<UserSearchResult>>
    setManualActiveFTUE: Action<StoreModel, string>
    setPreviousBackground: Action<StoreModel, string>

    // thunks
    updateUserAfterLogin: Thunk<StoreModel, UserData, {}, StoreModel>
    initUser: Thunk<StoreModel>
    updateUserFollowList: Thunk<StoreModel>
}

export const store = createStore<StoreModel>({
    // stores
    analysisMode: AnalysisViewModel,
    gameView: gameViewModel,
    emotes: EmotesModel,
    observeView: ObserveViewModel,
    puzzlesView: PuzzlesViewModel,
    dailyPuzzleView: DailyPuzzleViewModel,
    puzzleQuestView: PuzzleQuestViewModel,
    puzzlePracticeView: PuzzlePracticeViewModel,
    dailyPuzzleViewV2: DailyPuzzleViewV2Model,
    profileView: persist(ProfileViewModel, {
        allow: ['profileMenu', 'recordAmount', 'filter', 'gamesPerPage', 'sort', 'activePage', 'isNewUser'],
        storage: 'sessionStorage',
    }),
    popupService: PopupServiceModel,
    missionsService: MissionsServiceModel,
    dailyBonus: DailyBonusModel,
    ws: WebsocketModel,

    // state
    // TODO: Remove single state entities from global store
    drawerOpen: 'closed',
    isDrawerMenuOpen: true,
    serverConnectionData: false,
    darkMode: true,
    activeTab: 0,
    activeAnalysisTab: 0,
    queueTimeStarts: [],
    token: undefined,
    unreadMessages: 0,
    matchMaker: matchMakingConnectionModel,
    rules: [],
    userGroups: [],
    followList: [],
    PGNHeader: BasePGNHeaders,
    manualActiveFTUE: '',
    userData: { state: LoginState.INITIALIZING, userData: null, token: '' },
    snackbarMessage: undefined,
    previousBackground: '',

    // actions
    setDarkMode: action((state, on) => {
        state.darkMode = on
    }),

    setDrawerOpen: action((state, open) => {
        state.drawerOpen = open
    }),

    setIsDrawerMenuOpen: action((state, open) => {
        state.isDrawerMenuOpen = open
    }),

    setActiveTab: action((state, tab) => {
        state.activeTab = tab
    }),

    setActiveAnalysisTab: action((state, tab) => {
        state.activeAnalysisTab = tab
    }),

    setUserData: action((state, ud) => {
        state.userData = ud
    }),

    updateUserData: action((state, payload) => {
        state.userData.userData = payload
    }),

    setRules: action((state, groups) => {
        state.rules = Array.isArray(groups) ? groups : []
    }),

    setUserGroups: action((state, groups) => {
        state.userGroups = groups
    }),

    setPGNHeader: action((state, header) => {
        state.PGNHeader = header
    }),

    setUnreadMessages: action((state, num) => {
        state.unreadMessages = num
    }),

    setUpdatedRating: action((state, payload) => {
        if (state.userData.userData) {
            state.userData.userData.ratings = {
                ...state.userData.userData.ratings,
                [payload.type]: {
                    ...state.userData.userData.ratings[payload.type],
                    rating: payload.newRating,
                },
            }
        }
    }),

    setToken: action((state, token) => {
        state.token = token
    }),

    setServerConnectionData: action((state, data) => {
        state.serverConnectionData = data
    }),

    setSnackbarMessage: action((state, message) => {
        state.snackbarMessage = message
    }),

    addQueueTimeStart: action((state, time) => {
        state.queueTimeStarts.push(time)
    }),

    removeQueueTimeStart: action((state, challengeId) => {
        const foundQueueTimeStart = state.queueTimeStarts.find((t) => t.challengeId === challengeId)
        if (!foundQueueTimeStart) return

        state.queueTimeStarts = state.queueTimeStarts.filter((t) => t.challengeId !== challengeId)
    }),

    updateQueueTimeStart: action((state, data) => {
        const foundQueueTimeStart = state.queueTimeStarts.find((t) => t.challengeId === data.challengeId)
        if (foundQueueTimeStart) {
            foundQueueTimeStart.pairId = data.pairId
        }
    }),

    clearQueueTimeStarts: action((state) => {
        state.queueTimeStarts = []
    }),

    setUserFollowList: action((state, follows) => {
        state.followList = Array.isArray(follows) ? follows : []
    }),

    setManualActiveFTUE: action((state, ftue) => {
        state.manualActiveFTUE = ftue
    }),

    setPreviousBackground: action((state, previousBackground) => {
        state.previousBackground = previousBackground
    }),

    // thunks
    updateUserAfterLogin: thunk(async (actions, data, { getState }) => {
        if (!data) return
        const token = GetCookie(tokenCookieKey)
        // initUser() also runs on every appResume. When the resume confirms the
        // same user on the same token there is nothing to apply: re-running the
        // full hydration would overwrite game settings with the server copy
        // (harmless — every in-app settings writer already round-trips through
        // saveUserSettings — but wasted), refetch the follow list, and hand
        // GlobalProvider a fresh userData object to cascade on, per foreground.
        const current = getState()
        if (current.userData.state === LoginState.LOGGED_IN && current.userData.userData?.id === data.id) {
            if (token && current.token !== token) actions.setToken(token)
            return
        }
        actions.setUserData({
            state: LoginState.LOGGED_IN,
            userData: data,
            token: token,
        })
        actions.profileView.setCurrentAvatar(data.avatar_url || '')
        if (!!token) {
            actions.setToken(token)
        }
        if (data.groups) {
            const ruleNames = data.groups.flatMap((group) => group.rules.map((rule) => rule.name))
            actions.setRules(ruleNames)
            const userGroups = data.groups.map((group) => group.name)
            actions.setUserGroups(userGroups)
        }
        if (data.game_settings) {
            actions.gameView.setSettings({
                ...data.game_settings,
                boardStyle: boardColors[data.game_settings.boardStyle] ? data.game_settings.boardStyle : 'default',
            })
        }
        // Check isNewUser before determining
        const isNewUser = getState().profileView.isNewUser
        if (data.created_at && isNewUser) {
            actions.profileView.determineIfNewUser({
                date: data.created_at,
                seniority: data.user_config.main_menu_seniority,
            })
        }

        // Not awaited: nothing on first paint reads it. AMS answers 403 to guests.
        if (!isGuest(data.groups)) void actions.updateUserFollowList()
    }),

    initUser: thunk(async (actions, _, { getState }) => {
        const result = await ensureLogin()

        if (result.user) {
            await actions.updateUserAfterLogin(result.user)
            return
        }

        if (result.indeterminate) {
            // ensureLogin already retried once internally and still couldn't confirm
            // the session (network blip). This thunk runs on both cold boot AND
            // every appResume, so at this point there may already be a perfectly
            // live session with a connected token — wiping state.token here would
            // kill in-flight WebSocket reconnects and log a genuinely-fine user
            // out, reintroducing "random session loss" one layer up. Leave
            // everything untouched unless we're still in the pre-first-session
            // INITIALIZING state, where there's nothing live to disrupt; even then
            // this only stops the spinner — GlobalContext's 'online' listener
            // retries ensureLogin once connectivity genuinely returns.
            if (getState().userData.state === LoginState.INITIALIZING) {
                actions.setUserData({ state: LoginState.NOT_LOGGED_IN, userData: null, token: '' })
            }
            return
        }

        const guestData = await guestRegistration()
        // a status-0 response can resolve with an empty body: no user means the registration failed
        if (guestData?.user && !guestData.error) {
            await actions.updateUserAfterLogin(guestData.user)
            if (guestData.joined) return
            analyticsManager.dispatchEvent('completeSignup', {
                authType: 'guest',
                country: 'NA',
                userId: guestData.user.id,
                userName: guestData.user.user_name,
            })
        } else {
            actions.gameView.shutDownGSConnection()
            actions.gameView.resetGame(GameViewState.PRE_GAME)
            actions.matchMaker.reset()
            actions.setUserData({ state: LoginState.NOT_LOGGED_IN, userData: null, token: '' })
            actions.setToken('')
            actions.profileView.setIsNewUser(true)
        }
    }),

    updateUserFollowList: thunk(async (actions) => {
        const followsData = await getUserFollowList()
        actions.setUserFollowList(followsData)
    }),
})

export default store
